import { z } from 'zod';
import { OpenQuestion } from './agent-envelope.js';
import { AgentProviderSpec } from './llm-providers.js';
/**
 * Uploaded files (tracker task 32): evidence a business adds to its assessment. What a file is comes from
 * its bytes, never from the name or the browser's word for it. Only types the platform can read and that
 * carry no macros are taken; anything else is refused before it is scanned or stored.
 */
export const UPLOAD_TYPES = {
    'application/pdf': { ext: ['pdf'], label: 'PDF' },
    'image/png': { ext: ['png'], label: 'PNG image' },
    'image/jpeg': { ext: ['jpg', 'jpeg'], label: 'JPEG image' },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { ext: ['docx'], label: 'Word document' },
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { ext: ['xlsx'], label: 'Excel workbook' },
    'text/csv': { ext: ['csv'], label: 'CSV file' },
    'text/plain': { ext: ['txt'], label: 'Text file' },
}; // prettier-ignore
/** 25 MiB: a scanned policy schedule or a backup report, not a disk image. */
export const UPLOAD_MAX_BYTES = 25 * 1024 * 1024;
/** For a file input's `accept`: every extension the platform takes. */
export const UPLOAD_ACCEPT = Object.values(UPLOAD_TYPES)
    .flatMap((t) => t.ext.map((e) => `.${e}`))
    .join(',');
const startsWith = (b, sig) => sig.every((x, i) => b[i] === x);
const ascii = (s) => [...s].map((c) => c.charCodeAt(0));
/** Whether the bytes contain this ASCII text anywhere (zip entry names are stored as plain text). */
function contains(b, text) {
    const n = ascii(text);
    outer: for (let i = 0; i + n.length <= b.length; i++) {
        for (let j = 0; j < n.length; j++)
            if (b[i + j] !== n[j])
                continue outer;
        return true;
    }
    return false;
}
/**
 * What the file really is. The extension must agree with the bytes (a renamed executable is refused), and
 * Office files must be the macro-free OOXML kinds.
 */
export function sniffType(bytes, fileName) {
    if (!bytes.length)
        return { ok: false, reason: 'empty' };
    const ext = fileName.toLowerCase().split('.').pop() ?? '';
    let type = null;
    if (startsWith(bytes, ascii('%PDF-')))
        type = 'application/pdf';
    else if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]))
        type = 'image/png';
    else if (startsWith(bytes, [0xff, 0xd8, 0xff]))
        type = 'image/jpeg';
    else if (startsWith(bytes, [0x50, 0x4b, 0x03, 0x04])) {
        if (!contains(bytes, '[Content_Types].xml'))
            return { ok: false, reason: 'unsupported' };
        if (contains(bytes, 'vbaProject.bin'))
            return { ok: false, reason: 'macros' };
        if (contains(bytes, 'word/'))
            type = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        else if (contains(bytes, 'xl/'))
            type = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        else
            return { ok: false, reason: 'unsupported' };
    }
    else if (isText(bytes))
        type = ext === 'csv' ? 'text/csv' : 'text/plain';
    if (!type)
        return { ok: false, reason: 'unsupported' };
    return UPLOAD_TYPES[type].ext.includes(ext)
        ? { ok: true, type }
        : { ok: false, reason: 'mismatch' };
}
/** UTF-8 text with no NUL bytes or other control characters a text file wouldn't have. */
function isText(b) {
    for (let i = 0; i < b.length;) {
        const c = b[i];
        if (c < 0x80) {
            // Tab, line feed, vertical tab, form feed and carriage return are text; other controls aren't.
            if (c < 0x09 || (c > 0x0d && c < 0x20))
                return false;
            i++;
            continue;
        }
        const n = c >= 0xc2 && c <= 0xdf ? 1 : c >= 0xe0 && c <= 0xef ? 2 : c >= 0xf0 && c <= 0xf4 ? 3 : -1;
        if (n < 0 || i + n >= b.length)
            return false;
        for (let j = 1; j <= n; j++)
            if ((b[i + j] & 0xc0) !== 0x80)
                return false;
        i += n + 1;
    }
    return true;
}
/** Whether a question's `accept` list (MIME types, `image/*` wildcards) takes this type. */
export function acceptsType(accept, type) {
    return accept.some((a) => a === type || (a.endsWith('/*') && type.startsWith(a.slice(0, -1))));
}
/** A display name: the last path segment, without control characters, at most 200 characters. */
export function safeFileName(name) {
    const base = name.split(/[/\\]/).pop() ?? '';
    // eslint-disable-next-line no-control-regex
    const clean = base.replace(/[\u0000-\u001f\u007f]/g, '').trim();
    if (clean.length <= 200)
        return clean || 'file';
    const dot = clean.lastIndexOf('.');
    const ext = dot > 0 && clean.length - dot <= 10 ? clean.slice(dot) : '';
    return clean.slice(0, 200 - ext.length) + ext;
}
export const SNIFF_MESSAGES = {
    empty: 'The file is empty.',
    unsupported: `Upload a ${Object.values(UPLOAD_TYPES)
        .map((t) => t.label)
        .join(', ')}.`,
    mismatch: 'The file’s name doesn’t match what is in it. Upload it under its real type.',
    macros: 'Files with macros can’t be uploaded. Save it as a normal .docx or .xlsx and try again.',
};
// ---------- document types (task 33) ----------
/** Reminder offsets: days before the due date, as `T-<days>`. */
export const ReminderOffset = z.string().regex(/^T-\d{1,4}$/, 'T-<days>, e.g. T-120');
export const DEFAULT_REMINDER_OFFSETS = ['T-120', 'T-90', 'T-60', 'T-30', 'T-7'];
/** What a date starts: a renewal (the insurer's cycle), an expiry (a certificate) or a review (a policy). */
export const OBLIGATION_KINDS = ['renewal', 'expiry', 'review'];
export const ReminderSpec = z.strictObject({
    title: z.string().trim().min(1).max(200),
    kind: z.enum(OBLIGATION_KINDS).default('expiry'),
    offsets: z
        .array(ReminderOffset)
        .min(1)
        .max(10)
        .default([...DEFAULT_REMINDER_OFFSETS]),
});
export const DOCUMENT_FIELD_TYPES = ['text', 'date', 'number', 'boolean', 'list'];
/**
 * A kind of evidence document, published as configuration. The classifier sorts uploads into document
 * types using the name, description and cues; extraction (task 34) fills in the fields.
 */
export const DocumentTypeDefinition = z.strictObject({
    name: z.string().trim().min(1).max(200),
    description: z.string().max(1000).default(''),
    /** Words and phrases that usually appear in this kind of document (any language), lower case. */
    cues: z.array(z.string().trim().toLowerCase().min(2).max(100)).max(40).default([]),
    /** File types it usually comes as (MIME types; `image/*` allowed). */
    accept: z.array(z.string().min(3)).min(1).default(['application/pdf']),
    fields: z
        .array(z.strictObject({
        key: z.string().regex(/^[a-z][a-z0-9_]{0,39}$/, 'lower_snake_case, max 40'),
        label: z.string().trim().min(1).max(200),
        type: z.enum(DOCUMENT_FIELD_TYPES),
        description: z.string().max(500).default(''),
        /** A date field that starts an obligation, with reminders before it (task 39, decision 0024). */
        reminder: ReminderSpec.optional(),
    }))
        .max(30)
        .default([])
        .refine((fs) => new Set(fs.map((f) => f.key)).size === fs.length, 'field keys must be unique')
        .refine((fs) => fs.every((f) => !f.reminder || f.type === 'date'), 'only date fields have reminders'),
    /** The controls this kind of document can be evidence for. */
    controls: z.array(z.string().min(1)).default([]),
});
/** A document type as the classifier agent sees it (`DocumentTypeOption` in the Python service). */
export const AgentDocumentType = z.strictObject({
    key: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_.-]{0,63}$/),
    name: z.string().min(1).max(200),
    description: z.string().max(1000).default(''),
    cues: z.array(z.string()).max(40).default([]),
});
export const toAgentDocumentType = (key, d) => ({
    key,
    name: d.name,
    description: d.description,
    cues: d.cues,
});
/** What the core sends the classifier agent (`ClassifyRequest` in the Python service). */
export const ClassifyRequest = z.strictObject({
    tenant_id: z.string(),
    assessment_id: z.string(),
    document_id: z.string(),
    file_name: z.string().min(1).max(200),
    content_type: z.string().min(3).max(200),
    /** The file, base64. */
    content_base64: z.string().min(4).max(35_000_000),
    document_types: z.array(AgentDocumentType).min(1).max(100),
    provider: AgentProviderSpec.nullable().optional(),
});
// ---------- extraction (task 34) ----------
/** A document type as the extraction agent sees it: the fields to fill (`ExtractDocumentType` in Python). */
export const AgentExtractType = z.strictObject({
    key: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_.-]{0,63}$/),
    name: z.string().min(1).max(200),
    description: z.string().max(1000).default(''),
    fields: z
        .array(z.strictObject({
        key: z.string().regex(/^[a-z][a-z0-9_]{0,39}$/),
        label: z.string().min(1).max(200),
        type: z.enum(DOCUMENT_FIELD_TYPES),
        description: z.string().max(500).default(''),
    }))
        .max(30)
        .default([]),
});
export const toAgentExtractType = (key, d) => ({
    key,
    name: d.name,
    description: d.description,
    fields: d.fields.map((f) => ({ key: f.key, label: f.label, type: f.type, description: f.description })),
});
/**
 * What the core sends the extraction agent (`ExtractRequest` in the Python service): the file, its
 * document type's fields, and the assessment's questions it might answer (agent question shape).
 */
export const ExtractRequest = z.strictObject({
    tenant_id: z.string(),
    assessment_id: z.string(),
    document_id: z.string(),
    file_name: z.string().min(1).max(200),
    content_type: z.string().min(3).max(200),
    content_base64: z.string().min(4).max(35_000_000),
    /** Null when the classifier found no matching type ("other"): only the questions are answered. */
    document_type: AgentExtractType.nullable().default(null),
    questions: z.array(OpenQuestion).max(400).default([]),
    /** YYYY-MM-DD in Malaysia, for relative dates ("last month"). */
    today: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .default(null),
    provider: AgentProviderSpec.nullable().optional(),
});
// ---------- retrieval (task 38) ----------
/** What the core sends to have a document chunked and embedded (`IndexRequest` in Python). */
export const IndexRequest = z.strictObject({
    tenant_id: z.string(),
    document_id: z.string(),
    file_name: z.string().min(1).max(200),
    content_type: z.string().min(3).max(200),
    content_base64: z.string().min(4).max(35_000_000),
});
export const IndexResult = z.object({
    /** The embedding model: a query must be embedded by the same one to be compared. */
    model: z.string(),
    dims: z.number().int(),
    chunks: z.array(z.object({
        seq: z.number().int(),
        page: z.number().int(),
        text: z.string(),
        embedding: z.array(z.number()),
    })),
    problem: z.string().nullable().default(null),
});
export const EmbedRequest = z.strictObject({
    tenant_id: z.string(),
    texts: z.array(z.string()).min(1).max(16),
});
export const EmbedResult = z.object({
    model: z.string(),
    dims: z.number().int(),
    embeddings: z.array(z.array(z.number())),
});
/** Flags the classifier can raise, as `flag:<name>` proposals. */
export const DOCUMENT_FLAGS = [
    'out_of_scope',
    'unreadable',
    'instructions_in_text',
    'personal_data',
];
//# sourceMappingURL=documents.js.map